import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { Card } from '@/common/components/Card/Card';
import { Dialog } from '@/common/components/Dialog/Dialog';
import { Icon } from '@/common/components/Icons/Icon';
import { ImageInterop } from '@/common/components/ImageInterop/ImageInterop';
import { LabeledValue } from '@/common/components/LabeledValue/LabeledValue';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { Stack } from '@/common/components/Stack/Stack';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { TInsurancePolicyOperator } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { formatIncompletePhoneNumber } from 'libphonenumber-js';
import { styles } from './PolicyOperatorDetailsCard.styles';

export const PolicyOperatorDetailsCard = ({
  operator,
  loading,
}: TPolicyOperatorDetailsCardProps) => {
  const { t } = useTranslation();
  const { lg } = useBreakpoints(['lg']);
  const [showDialog, setShowDialog] = useState(false);

  const handleOnPress = () => {
    if (lg) {
      if (operator?.url) {
        Linking.openURL(operator.url);
      }
    } else {
      setShowDialog(true);
    }
  };

  const formattedPhone = operator?.phone
    ? formatIncompletePhoneNumber(operator.phone)
    : '';

  const operatorDetailsCard = (
    <Card plain className={styles.root}>
      <Stack gap="section" grow>
        <Stack gap="sm">
          {loading ? (
            <SkeletonInline className={styles.skeleton} />
          ) : (
            operator?.logoUrl && (
              <Stack className={styles.skeleton}>
                <ImageInterop
                  source={operator.logoUrl}
                  alt={operator.name}
                  contentFit="contain"
                  className={styles.img}
                />
              </Stack>
            )
          )}
          {loading ? (
            <SkeletonInline className={styles.skeleton} />
          ) : (
            <BodyText size="sm">
              {t('dashboard.insurance_detail.card.operator.description', {
                insurance_operator: operator?.name,
              })}
            </BodyText>
          )}
        </Stack>
        <Stack gap="md">
          {(loading || formattedPhone) && (
            <LabeledValue
              label={t('dashboard.insurance_detail.card.operator.phone')}
              value={
                formattedPhone && (
                  <TextAction href={`tel:${formattedPhone}`} type="label">
                    {formattedPhone}
                  </TextAction>
                )
              }
              isLoading={loading}
            />
          )}
          {(loading || operator?.email) && (
            <LabeledValue
              label={t('dashboard.insurance_detail.card.operator.email')}
              value={
                operator?.email && (
                  <TextAction href={`mailto:${operator.email}`} type="label">
                    {operator.email}
                  </TextAction>
                )
              }
              isLoading={loading}
            />
          )}
        </Stack>
        {(loading || operator?.url) && (
          <Button
            variant="secondary"
            icon={<Icon icon="arrow-up-right" />}
            iconAlign="right"
            disabled={loading}
            onPress={handleOnPress}
            className={styles.button}
          >
            {t('dashboard.insurance_detail.card.operator.button')}
          </Button>
        )}
      </Stack>
    </Card>
  );

  if (loading) {
    return operatorDetailsCard;
  }

  return (
    <>
      {showDialog && (
        <Dialog
          iconBadgeColor="accentSecondary20"
          icon={<Icon icon="hospital" />}
          title={t('dashboard.insurance_detail.card.operator.modal.title')}
          primaryAction={{
            children: t(
              'dashboard.insurance_detail.card.operator.modal.continue.btn'
            ),
            onPress: (closeDialog) => {
              if (operator?.url) Linking.openURL(operator.url);
              closeDialog();
            },
          }}
          secondaryAction={{
            children: t('general.navigation.cancel'),
          }}
          onClose={() => setShowDialog(false)}
        />
      )}
      {operatorDetailsCard}
    </>
  );
};

export type TPolicyOperatorDetailsCardProps = {
  operator?: TInsurancePolicyOperator;
  loading?: boolean;
};
